import { useEffect, useState } from 'react';
import { Route, Routes, useMatch, useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { RouterTabs } from '@components/header/router-tabs';
import { IconPersonDetails } from '@components/icons/icon-person-details';
import { IconReward } from '@components/icons/reward';
import { Spinner } from '@components/loading-spinner';

import { api } from 'api';

import { Box } from 'components/Box';
import { Header } from 'components/headers';
import IntegrationList from 'components/IntegrationList';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import PlaceholderPage from 'components/PlaceholderPage';
import { useEnsureJobLoaded } from 'hooks/useEnsureJobLoaded';
import { useStore } from 'models/Provider';

import ApplicationOnboardingDetails from './ApplicationOnboardingDetails';
import Integrations from './Integrations';
import Rewards from './Rewards';
import Tasklist from './Tasklist';

export const ApplicationOnboarding = observer(() => {
  const { applicationStore, current, t } = useStore();
  const [updating, setUpdating] = useState(false);
  const { jobID, applicationID } = useMatch('/jobs/:jobID/onboarding/:applicationID/*').params;
  const integrationID = useMatch(
    '/jobs/:jobID/onboarding/:applicationID/integrations/:integrationID'
  )?.params.integrationID;
  useEnsureJobLoaded(jobID);

  const { useSsnRegistry } = current.info;
  const application = applicationStore.items.get(applicationID);

  useEffect(() => {
    if (!application || !application.loaded) {
      applicationStore.load(applicationID);
    }
  }, [applicationID, applicationStore, application]);

  const loadIntegrations = useQuery({
    queryKey: ['application-onboarding-load', applicationID],
    queryFn: () => api.applications.loadIntegrations(applicationID, 'applicant_hired'),
    onSuccess: (data) => application.setIntegrations(data),
    enabled: Boolean(application),
  });

  if (!application) {
    return <PlaceholderPage />;
  }
  const job = application.job;

  async function markOnboarded() {
    setUpdating(true);
    await application.markOnboarded(true);
    setUpdating(false);
  }

  async function unmarkOnboarded() {
    setUpdating(true);
    await application.markOnboarded(false);
    setUpdating(false);
  }

  return (
    <div>
      <Header
        mobileTitle={application.name}
        tabs={<ApplicationOnboardingTabs application={application} />}
        breadcrumbs={
          application.loaded ? <ApplicationOnboardingCrumbs application={application} /> : null
        }
      />
      <PageContent>
        <GridContainer>
          <IntegrationListGridColum>
            {loadIntegrations.isSuccess && (
              <IntegrationList
                url={application.onboardingUrl}
                triggers={application.integrations}
                activeId={integrationID ? Number(integrationID) : undefined}
                loading={loadIntegrations.isFetching}
                t={t}
                withRatio
              />
            )}
            {loadIntegrations.isLoading && <Spinner />}
          </IntegrationListGridColum>
          <IntegrationDetailGridColum>
            <Routes>
              <Route
                path="integrations/:integrationID"
                element={<IntegrationDetail application={application} />}
              />
              <Route
                path="rewards"
                element={<Rewards job={job} application={application} t={t} />}
              />
              <Route
                path="*"
                element={
                  <ApplicationOnboardingDetails
                    application={application}
                    useSsnRegistry={useSsnRegistry}
                  />
                }
              />
            </Routes>
          </IntegrationDetailGridColum>
          <TaskListGridColum>
            {updating && <Loading />}
            {!updating && (
              <Box mb="24">
                {application.isOnboarded && (
                  <Button
                    variant="secondary"
                    style={{ width: '100%' }}
                    onClick={unmarkOnboarded}
                    text={t('application.onboarding.unmark_onboarded')}
                  />
                )}
                {!application.isOnboarded && (
                  <Button
                    style={{ width: '100%' }}
                    onClick={markOnboarded}
                    text={t('application.onboarding.mark_onboarded')}
                  />
                )}
              </Box>
            )}
            <Tasklist application={application} t={t} />
          </TaskListGridColum>
        </GridContainer>
      </PageContent>
    </div>
  );
});

export default ApplicationOnboarding;

function IntegrationDetail({ application }) {
  const { integrationID } = useParams();

  const id = Number(integrationID);
  const integration = application.integrations.find((i) => i.triggerId === id);
  if (!integration) {
    return null;
  }
  return <Integrations key={integrationID} integration={integration} />;
}

const ApplicationOnboardingTabs = observer(({ application }) => {
  const { t } = useStore();
  const section = useMatch('/jobs/:jobID/onboarding/:applicationID/:section/*')?.params.section;

  return (
    <RouterTabs
      ariaLabel={t('application.onboarding.tabs.label')}
      activeSection={section ?? 'details'}
      tabs={[
        {
          label: t('application.onboarding.tabs.details'),
          to: application.onboardingDetailsUrl,
          section: 'details',
          icon: <IconPersonDetails />,
        },
        {
          label: t('application.onboarding.tabs.sourcing'),
          to: application.onboardingRewardsUrl,
          section: 'rewards',
          icon: <IconReward />,
        },
      ]}
    />
  );
});

const ApplicationOnboardingCrumbs = observer(({ application }) => {
  const { t } = useStore();
  const job = application.job;

  return (
    <Breadcrumb
      items={[
        { label: t('navigation.jobs'), link: '/jobs' },
        { label: job.title, link: job.detailUrl },
        { label: application.name, link: application.detailUrl },
        { label: t('application.onboarding.crumb.onboard') },
      ]}
    />
  );
});

const GridContainer = styled.div((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gridColumnGap: '1.5em',
  '@media(max-width: 1280px)': {
    gridTemplateColumns: 'repeat(10, 1fr)',
  },
  '@media(max-width: 768px)': {
    gridTemplateColumns: 'repeat(6, 1fr)',
  },
  '@media(max-width: 375px)': {
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridColumnGap: '1em',
  },
}));

const IntegrationListGridColum = styled.div((p) => ({
  gridColumn: '1 / span 3',

  '@media(max-width: 1280px)': {
    gridColumn: '1 / span 3',
  },
  '@media(max-width: 1080px)': {
    gridColumn: '1 / span 4',
  },
  '@media(max-width: 768px)': {
    gridColumn: '1 / span 6',
    marginBottom: '1.5rem',
  },
  '@media(max-width: 375px)': {
    gridColumn: '1 / span 4',
  },
}));

const IntegrationDetailGridColum = styled.div((p) => ({
  gridColumn: '5 / span 4',

  '@media(max-width: 1280px)': {
    gridColumn: '4 / span 4',
  },
  '@media(max-width: 1080px)': {
    gridColumn: '5 / span 6',
  },
  '@media(max-width: 768px)': {
    gridColumn: '1 / span 6',
    maxWidth: '100%',
    marginBottom: '1.5rem',
  },
  '@media(max-width: 375px)': {
    gridColumn: '1 / span 4',
  },
}));

const TaskListGridColum = styled.div((p) => ({
  gridColumn: '10 / span 3',

  '@media(max-width: 1280px)': {
    gridColumn: '8 / span 3',
  },
  '@media(max-width: 1080px)': {
    gridColumn: '1 / span 4',
  },
  '@media(max-width: 768px)': {
    gridColumn: '1 / span 6',
    maxWidth: '100%',
  },
  '@media(max-width: 375px)': {
    gridColumn: '1 / span 4',
    maxWidth: '100%',
  },
}));
